import { useState } from 'react'

import { useSaveSettings, useSettings } from '../../hooks/queries'
import type { Zahlenwert } from '../../api/types'

/**
 * Bildgröße und Grenzen -- einstellbar ohne FTP.
 *
 * Bisher standen diese Zahlen in der `.env` und die Bildgröße sogar fest in
 * der ausgelieferten Releaseversion: eine Änderung hieß neu bauen, hochladen, hoffen.
 * Unterwegs, mit dem Telefon in der Hand, ist das kein Weg.
 *
 * **Die Karte zeigt sich nur der Verwaltung** -- und zwar, weil der Server
 * die Decke mitschickt, nicht weil die Oberfläche aus der Rolle schließt.
 * Wer sie nicht bekommt, sieht nichts; wer sie an den Server vorbei aufruft,
 * bekommt vom Server ein Nein.
 */
export function LimitsCard() {
  const settings = useSettings()
  const save = useSaveSettings()
  // Nur die Zahlen: die Farbe der Route hat eine eigene Karte, und ein
  // gemeinsamer Entwurf müsste beide Arten von Wert aushalten.
  const [entwurf, setEntwurf] = useState<Partial<Record<Zahlenwert, string>>>({})
  const [gemeldet, setGemeldet] = useState(false)

  const daten = settings.data
  // Ohne Decke ist dieses Konto keine Verwaltung -- der Server hat entschieden.
  if (!daten?.ceiling || !daten.limits) return null

  const werte = daten.settings
  const decke = daten.ceiling
  const grenzen = daten.limits

  function wert(name: Zahlenwert): string {
    return entwurf[name] ?? String(werte[name])
  }

  function geaendert(): Partial<Record<Zahlenwert, number>> {
    const teil: Partial<Record<Zahlenwert, number>> = {}
    for (const [name, roh] of Object.entries(entwurf)) {
      const zahl = Number(roh)
      if (!Number.isInteger(zahl)) continue
      if (zahl === werte[name as Zahlenwert]) continue
      teil[name as Zahlenwert] = zahl
    }
    return teil
  }

  const offen = Object.keys(geaendert()).length > 0

  function speichern() {
    setGemeldet(false)
    save.mutate(geaendert(), {
      onSuccess: () => {
        setEntwurf({})
        setGemeldet(true)
      },
    })
  }

  return (
    <div className="card card--seite">
      <h2 className="card__titel">Bilder, Videos, Platz</h2>
      <p className="card__lead">
        Gilt für alle Reisen. Die Bildgröße wirkt beim nächsten Foto, die
        Grenzen sofort.
      </p>

      <Feld
        name="image_max_edge"
        label="Lange Kante der Fotos"
        einheit="Pixel"
        hinweis="Darunter wird es körnig, darüber wächst der Platzbedarf schneller als der Nutzen."
        wert={wert('image_max_edge')}
        decke={decke.image_max_edge}
        grenzen={grenzen.image_max_edge}
        onChange={(v) => setEntwurf({ ...entwurf, image_max_edge: v })}
      />
      <Feld
        name="image_quality"
        label="Bildqualität"
        einheit="%"
        hinweis="Unter 60 sieht man es, über 90 zahlt man viel Platz für wenig."
        wert={wert('image_quality')}
        decke={decke.image_quality}
        grenzen={grenzen.image_quality}
        onChange={(v) => setEntwurf({ ...entwurf, image_quality: v })}
      />
      <Feld
        name="image_max_mb"
        label="Größtes Foto"
        einheit="MB"
        wert={wert('image_max_mb')}
        decke={decke.image_max_mb}
        grenzen={grenzen.image_max_mb}
        onChange={(v) => setEntwurf({ ...entwurf, image_max_mb: v })}
      />
      <Feld
        name="video_max_mb"
        label="Größtes Video"
        einheit="MB"
        hinweis="Videos gehen unverändert raus — hier entscheidet sich, was der Teilen-Dialog liefern darf."
        wert={wert('video_max_mb')}
        decke={decke.video_max_mb}
        grenzen={grenzen.video_max_mb}
        onChange={(v) => setEntwurf({ ...entwurf, video_max_mb: v })}
      />
      <Feld
        name="trip_quota_mb"
        label="Platz je Reise"
        einheit="MB"
        wert={wert('trip_quota_mb')}
        decke={decke.trip_quota_mb}
        grenzen={grenzen.trip_quota_mb}
        onChange={(v) => setEntwurf({ ...entwurf, trip_quota_mb: v })}
      />

      {/* Warum ein Wert wieder kleiner dasteht, als man ihn eingetragen hat.
          Ohne diesen Satz hielte man das für einen Fehler. */}
      <p className="feld__hinweis">
        Höher als die Werte in der <code>.env</code> geht es nicht — was
        darüber liegt, wird auf die Grenze gesetzt. Nur dort lässt sich der
        Deckel anheben.
      </p>

      {save.isError && (
        <p className="notice" role="alert">
          {save.error instanceof Error ? save.error.message : 'Das hat nicht geklappt.'}
        </p>
      )}
      {gemeldet && !save.isPending && (
        <p className="card__lead" style={{ color: 'var(--accent)' }}>Gespeichert.</p>
      )}

      <button
        className="button button--outline"
        type="button"
        disabled={!offen || save.isPending}
        onClick={speichern}
      >
        {save.isPending ? 'Einen Moment …' : 'Speichern'}
      </button>
    </div>
  )
}

function Feld({
  name,
  label,
  einheit,
  hinweis,
  wert,
  decke,
  grenzen,
  onChange,
}: {
  name: string
  label: string
  einheit: string
  hinweis?: string
  wert: string
  decke: number
  grenzen: { min: number; max: number }
  onChange: (wert: string) => void
}) {
  return (
    <label className="field" style={{ marginBottom: 12 }}>
      <span className="field__label">
        {label} ({einheit})
      </span>
      <input
        className="field__input"
        type="number"
        inputMode="numeric"
        name={name}
        value={wert}
        min={grenzen.min}
        max={Math.min(grenzen.max, decke)}
        onChange={(event) => onChange(event.target.value)}
      />
      <span className="feld__hinweis">
        {hinweis ? `${hinweis} ` : ''}
        Höchstens {decke} laut <code>.env</code>.
      </span>
    </label>
  )
}
